import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAsyncTaskQueue } from "@alaffia-technology-solutions/hooks";

import type { PDFAnnot, PDFRatioCoords } from "../../helper-types";

import { usePDFStore } from "../../providers";
import { usePDF } from "../../use-pdf";
import { usePDFViewerEvent } from "../../use-pdf-viewer-event";
import { mapRatioToHighlightCoords } from "../../utils";

interface PDFAnnotationProps {
  pageIndex: number;
  coordinates: PDFRatioCoords[];
  color: string;
  onClick?: (annot: PDFAnnot) => unknown;
}

type PDFAnnotationRef = PDFAnnot | null;

export const PDFAnnotation = ({
  ref,
  pageIndex,
  coordinates,
  color,
  onClick,
}: PDFAnnotationProps & {
  ref?: React.RefObject<PDFAnnotationRef>;
}) => {
  const { currentPDFAnnot } = usePDFAnnotationRender({
    pageIndex,
    coordinates,
    color,
  });

  usePDFAnnotationColorSync({ currentPDFAnnot, pageIndex, color });

  usePDFAnnotationClickHandler({ currentPDFAnnot, onClick });

  useImperativeHandle<PDFAnnotationRef, PDFAnnotationRef>(
    ref,
    () => currentPDFAnnot,
  );

  return null;
};

interface UsePDFAnnotationRenderOptions {
  pageIndex: number;
  coordinates: PDFRatioCoords[];
  color: string;
}

function usePDFAnnotationRender({
  pageIndex,
  coordinates,
  color,
}: UsePDFAnnotationRenderOptions) {
  const [currentPDFAnnot, setCurrentPDFAnnot] = useState<PDFAnnot | null>(null);

  const { addHighlight, removeHighlights, getPageDimensions } = usePDF();

  const { enqueueTask } = useAsyncTaskQueue<PDFAnnot | null>();

  const coordinatesKey = useMemo(
    () => JSON.stringify(coordinates),
    [coordinates],
  );

  useEffect(() => {
    enqueueTask(
      async () => {
        const pageDimensions = await getPageDimensions(pageIndex);
        if (!pageDimensions) {
          throw new Error(`Failed to get page ${pageIndex} dimensions`);
        }

        const highlightCoordinates = coordinates.map((coords) =>
          mapRatioToHighlightCoords(coords, pageDimensions),
        );
        const addedHighlight = await addHighlight(
          pageIndex,
          highlightCoordinates,
          {
            color,
          },
        );
        if (!addedHighlight) {
          throw new Error("Failed to add highlight");
        }
        setCurrentPDFAnnot(addedHighlight);
        return addedHighlight;
      },
      async (highlight) => {
        if (highlight) {
          await removeHighlights([highlight]);
        }
      },
    );

    // We put stringified coordinates (coordinatesKey) as a dependency key instead of using coordinates itself,
    // so we need to disable exhaustive-deps eslint rule here.
    // We also don't want to rerun this effect when color prop changes because we handle color prop change separately,
    // so we don't add it to the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addHighlight,
    removeHighlights,
    getPageDimensions,
    enqueueTask,
    pageIndex,
    coordinatesKey,
  ]);

  return {
    currentPDFAnnot,
  };
}

interface UsePDFAnnotationColorSyncOptions {
  currentPDFAnnot: PDFAnnot | null;
  pageIndex: number;
  color: string;
}

function usePDFAnnotationColorSync({
  currentPDFAnnot,
  pageIndex,
  color,
}: UsePDFAnnotationColorSyncOptions) {
  const { data: currentPageIndex } = usePDFStore(
    (store) => store.currentPageIndex,
  );
  const previousColorRef = useRef(color);

  useEffect(() => {
    if (
      !currentPDFAnnot ||
      color === previousColorRef.current ||
      // Changing color of annotations that is not currently visible results in Worker error in Foxit v11
      currentPageIndex !== pageIndex
    ) {
      return;
    }

    void currentPDFAnnot.setBorderColor(color).catch(console.error);
    previousColorRef.current = color;
  }, [currentPDFAnnot, color, currentPageIndex, pageIndex]);
}

interface UsePDFAnnotationClickHandlerOptions {
  currentPDFAnnot: PDFAnnot | null;
  onClick?: (pdfAnnot: PDFAnnot) => unknown;
}

function usePDFAnnotationClickHandler({
  currentPDFAnnot,
  onClick,
}: UsePDFAnnotationClickHandlerOptions) {
  const { events } = usePDF();
  usePDFViewerEvent(
    events?.tapAnnotation,
    (annotRender: { getAnnot: () => PDFAnnot }) => {
      const eventAnnot = annotRender.getAnnot();
      if (eventAnnot.getUniqueID() === currentPDFAnnot?.getUniqueID()) {
        return onClick?.(eventAnnot);
      }
    },
  );
}
